import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

import type { PlaybookRunDetail } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { cn, formatDuration, relativeAge } from "@/lib/utils";

interface Props {
  run: PlaybookRunDetail;
  defaultExpanded?: boolean;
}

export function RunDetail({ run, defaultExpanded = false }: Props) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <div className="rounded-md border bg-card">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent/40"
        aria-expanded={expanded}
      >
        {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        <span className="font-mono text-xs text-muted-foreground">
          {run.run_id.slice(0, 12)}…
        </span>
        <span className="font-medium">{run.playbook_id}</span>
        <ModeBadge mode={run.mode} />
        <RunStatusBadge status={run.status} />
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {relativeAge(run.started_at)} · {formatDuration(run.duration_ms)}
        </span>
      </button>
      {expanded && (
        <div className="space-y-3 border-t px-4 py-3 text-sm">
          <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
            <Field label="run_id" value={run.run_id} mono />
            <Field label="started_at" value={new Date(run.started_at).toLocaleString()} />
            <Field
              label="finished_at"
              value={run.finished_at ? new Date(run.finished_at).toLocaleString() : "—"}
            />
            <Field label="duration" value={formatDuration(run.duration_ms)} />
            <Field label="notification" value={run.notification_status ?? "—"} />
            {run.notes && <Field label="notes" value={run.notes} />}
          </div>
          <div>
            <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
              Steps ({run.steps.length})
            </div>
            <pre className="max-h-96 overflow-auto rounded bg-muted/50 p-3 text-xs leading-relaxed">
              <code>{JSON.stringify(run.steps, null, 2)}</code>
            </pre>
          </div>
          {run.rollback && (
            <div>
              <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                Rollback
              </div>
              <pre className="max-h-64 overflow-auto rounded bg-muted/50 p-3 text-xs">
                <code>{JSON.stringify(run.rollback, null, 2)}</code>
              </pre>
            </div>
          )}
          {run.approval && (
            <div>
              <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                Approval
              </div>
              <pre className="max-h-64 overflow-auto rounded bg-muted/50 p-3 text-xs">
                <code>{JSON.stringify(run.approval, null, 2)}</code>
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-muted-foreground">{label}</div>
      <div className={cn("break-all text-foreground", mono && "font-mono text-[11px]")}>
        {value}
      </div>
    </div>
  );
}

export function ModeBadge({ mode }: { mode: string }) {
  if (mode === "dry_run") {
    return (
      <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200">dry-run</Badge>
    );
  }
  if (mode === "live") {
    return <Badge className="bg-green-600 text-white hover:bg-green-600">live</Badge>;
  }
  return <Badge variant="outline">{mode}</Badge>;
}

export function RunStatusBadge({ status }: { status: string }) {
  const map: Record<string, { className: string; label: string }> = {
    success: { className: "bg-green-600 text-white", label: "success" },
    dry_run: { className: "bg-amber-200 text-amber-900", label: "dry-run" },
    failure: { className: "bg-red-600 text-white", label: "failure" },
    rolled_back: { className: "bg-red-600 text-white", label: "rolled-back" },
    skipped: { className: "bg-muted text-muted-foreground", label: "skipped" },
  };
  const entry = map[status] ?? { className: "bg-muted text-muted-foreground", label: status };
  return (
    <Badge className={cn(entry.className, "hover:opacity-100")}>{entry.label}</Badge>
  );
}
